'use client';

import { useCallback, useState } from 'react';

import { showWarningToast } from '@/components/ui/sonner';

const MAX_IMAGE_SIZE_BYTES = 30 * 1024 * 1024;

export type ProfileImageCropState = {
  imageSrc: string | null;
  isOpen: boolean;
  openWithFile: (file: File) => boolean;
  close: () => void;
  onOpenChange: (open: boolean) => void;
  toCroppedFile: (blob: Blob) => File;
};

/**
 * File pick → object URL → crop dialog lifecycle for profile media.
 */
export function useProfileImageCrop(): ProfileImageCropState {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [fileBaseName, setFileBaseName] = useState('image');
  const [isOpen, setIsOpen] = useState(false);

  const revokeCurrentSrc = useCallback((src: string | null) => {
    if (src?.startsWith('blob:')) {
      URL.revokeObjectURL(src);
    }
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setImageSrc((prev) => {
      revokeCurrentSrc(prev);
      return null;
    });
  }, [revokeCurrentSrc]);

  const onOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        close();
        return;
      }
      setIsOpen(true);
    },
    [close],
  );

  const openWithFile = useCallback(
    (file: File): boolean => {
      if (!file.type.startsWith('image/')) {
        showWarningToast('Файл должен быть изображением');
        return false;
      }
      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        showWarningToast('Размер файла должен быть меньше 30MB');
        return false;
      }

      const nextSrc = URL.createObjectURL(file);
      const baseName = file.name.replace(/\.[^.]+$/, '').trim() || 'image';

      setImageSrc((prev) => {
        revokeCurrentSrc(prev);
        return nextSrc;
      });
      setFileBaseName(baseName);
      setIsOpen(true);
      return true;
    },
    [revokeCurrentSrc],
  );

  const toCroppedFile = useCallback(
    (blob: Blob): File => {
      return new File([blob], `${fileBaseName}.webp`, { type: blob.type || 'image/webp' });
    },
    [fileBaseName],
  );

  return {
    imageSrc,
    isOpen,
    openWithFile,
    close,
    onOpenChange,
    toCroppedFile,
  };
}
